import { useCallback, useEffect, useRef, useState } from "react";
import JsBarcode from "jsbarcode";
import { CloseCircleFilled, DeleteOutlined, DownloadOutlined } from "@ant-design/icons";
import NoDataPlaceholder from "./common/NoDataPlaceholder";
import ErrorPlaceholder from "./common/ErrorPlaceholder";
import { useNote, TheNote, TheNoteToggleButton } from "./common/TheNote";
import { ItemSizeType } from "./elements/SizeSelect";

interface RenderOneBarcodeProps {
  data: string;
  index: number;
  itemSize?: ItemSizeType;
  onDataChange: (index: number, value: string) => void;
  onClear: () => void;
  showRemove: boolean;
  onRemove: () => void;
}

function RenderOneBarcode({
  data,
  index,
  itemSize,
  onDataChange,
  onClear,
  showRemove,
  onRemove,
}: RenderOneBarcodeProps) {
  const [barcodeInstance, setBarcodeInstance] = useState<HTMLCanvasElement | null>(null);
  const [debouncedData, setDebouncedData] = useState(data);
  const [hasError, setHasError] = useState(false);
  const { showNote, setShowNote, note, setNote } = useNote("barcode", index);
  const containerRef = useRef<HTMLDivElement>(null);
  const barcodeInstanceRef = useRef<HTMLCanvasElement | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Debounce data changes
  useEffect(() => {
    if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
    debounceTimeoutRef.current = setTimeout(() => setDebouncedData(data), 300);
    return () => { if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current); };
  }, [data]);

  // Create barcode instance when debounced data changes
  useEffect(() => {
    if (barcodeInstanceRef.current) {
      try {
        if (containerRef.current) containerRef.current.innerHTML = "";
      } catch (error) {
        console.error("Error cleaning up previous barcode instance:", error);
        if (containerRef.current) {
          try { containerRef.current.innerHTML = ""; } catch (clearError) {
            console.error("Error force clearing container:", clearError);
          }
        }
      }
    }

    if (containerRef.current) {
      try {
        if (!debouncedData || !String(debouncedData).trim()) {
          // Placeholder is handled in JSX
          containerRef.current.innerHTML = "";
        } else {
          containerRef.current.innerHTML = "";
        }
      } catch (error) {
        console.error("Error handling container:", error);
      }
    }

    if (debouncedData && String(debouncedData).trim()) {
      try {
        const trimmedData = String(debouncedData).trim();
        const canvas = document.createElement("canvas");
        const scale = 2;
        canvas.width = 800 * scale;
        canvas.height = 200 * scale;
        canvas.style.width = "800px";
        canvas.style.height = "200px";
        JsBarcode(canvas, trimmedData, {
          format: "CODE128",
          width: 3 * scale,
          height: 120 * scale,
          displayValue: true,
          fontSize: 16 * scale,
          margin: 15 * scale,
          background: "#ffffff",
          lineColor: "#000000",
          textAlign: "center",
          textPosition: "bottom",
          textMargin: 8 * scale,
        });
        setBarcodeInstance(canvas);
        barcodeInstanceRef.current = canvas;
        setHasError(false);
      } catch (error) {
        console.error(`Error creating barcode for data ${index}:`, error);
        setBarcodeInstance(null);
        barcodeInstanceRef.current = null;
        setHasError(true);
        if (containerRef.current) {
          try { containerRef.current.innerHTML = ""; } catch (clearError) {
            console.error("Error clearing container after barcode creation failure:", clearError);
          }
        }
      }
    } else {
      setBarcodeInstance(null);
      barcodeInstanceRef.current = null;
      setHasError(false);
    }
  }, [debouncedData, index]);

  // Render barcode
  useEffect(() => {
    if (barcodeInstance && containerRef.current) {
      const timeoutId = setTimeout(() => {
        try {
          if (containerRef.current) containerRef.current.innerHTML = "";
          const barcodeWrapper = document.createElement("div");
          barcodeWrapper.style.width = "100%";
          barcodeWrapper.style.height = "auto";
          if (containerRef.current) {
            containerRef.current.appendChild(barcodeWrapper);
            barcodeWrapper.appendChild(barcodeInstance);
          }
        } catch (error) {
          console.error(`Error rendering barcode ${index}:`, error);
          if (containerRef.current) {
            try { containerRef.current.innerHTML = ""; } catch (clearError) {
              console.error("Error clearing container after render failure:", clearError);
            }
          }
        }
      }, 100);
      return () => clearTimeout(timeoutId);
    } else if (!barcodeInstance && containerRef.current) {
      try {
        // Placeholder is handled in JSX
        containerRef.current.innerHTML = "";
      } catch (error) {
        console.error("Error showing placeholder:", error);
      }
    }
  }, [barcodeInstance, index]);

  // Cleanup on unmount
  useEffect(() => {
    const container = containerRef.current;
    return () => {
      if (container) {
        try { container.innerHTML = ""; } catch (error) { /* ignore */ }
      }
    };
  }, []);

  const handleInputClick = useCallback((e: React.MouseEvent<HTMLInputElement>) => { (e.target as HTMLInputElement).select(); }, []);

  const handleClearClick = useCallback(() => {
    onClear();
    setTimeout(() => { if (inputRef.current) inputRef.current.focus(); }, 10);
  }, [onClear]);

  const handleDownloadPNG = useCallback(() => {
    if (barcodeInstance) {
      const link = document.createElement("a");
      link.download = `barcode-${index + 1}.png`;
      link.href = barcodeInstance.toDataURL("image/png");
      link.click();
    }
  }, [barcodeInstance, index]);

  return (
    <div className={`qr-preview size-${itemSize || "medium"} relative card h-100 p-4`}>
      {/* Note */}
      <TheNote showNote={showNote} note={note} setNote={setNote} />

      {/* Input row: [input + clear X] */}
      <div style={{ position: "relative" }}>
        <input
          ref={inputRef}
          type="text"
          className="form-control highlight"
          value={data}
          name={"barcodeData" + index}
          onChange={(e) => onDataChange(index, e.target.value)}
          onClick={handleInputClick}
          placeholder="Barcode data..."
          style={{
            borderWidth: "2px",
            paddingTop: "2px",
            paddingBottom: "2px",
            paddingRight: data ? "26px" : undefined,
          }}
        />
        {data && (
          <button
            type="button"
            onClick={handleClearClick}
            title="Clear input"
            style={{
              position: "absolute",
              right: "8px",
              top: "50%",
              transform: "translateY(-50%)",
              background: "none",
              border: "none",
              padding: "2px",
              cursor: "pointer",
              color: "#bbb",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              lineHeight: 1,
              transition: "color 0.2s ease",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "#666")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "#bbb")}
          >
            <CloseCircleFilled style={{ fontSize: "15px" }} />
          </button>
        )}
      </div>

      {/* DISPLAY */}
      <div
        className="qr-display w-100"
        style={{
          paddingBottom: "100%",
          border: "2px solid #c5c9cc",
          borderRadius: "12px",
          position: "relative",
          overflow: "hidden"
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "10px",
          }}
        >
          {(!debouncedData || !String(debouncedData).trim()) && !hasError && (
            <NoDataPlaceholder
              onClick={() => { if (inputRef.current) inputRef.current.focus(); }}
              onMouseEnter={() => { if (inputRef.current) inputRef.current.placeholder = "Enter data here!"; }}
            />
          )}
          {hasError && (
            <ErrorPlaceholder
              message="Cannot encode this barcode"
              onClick={() => { if (inputRef.current) inputRef.current.focus(); }}
              onMouseEnter={() => { if (inputRef.current) inputRef.current.placeholder = "Enter data here!"; }}
            />
          )}
          <div
            ref={containerRef}
            style={{
              width: "100%",
              height: "100%",
              display: (!debouncedData || !String(debouncedData).trim() || hasError) ? "none" : "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          ></div>
        </div>
      </div>

      {/* Actions */}
      <div className="w-100" style={{ display: "flex", gap: "15px", justifyContent: "space-between" }}>
        <button
          type="button"
          className="btn btn-outline-danger btn-sm rounded-pill d-flex align-items-center justify-content-center p-0"
          style={{ width: "36px", height: "36px", flexShrink: 0 }}
          onClick={onRemove}
          title="Remove barcode"
          disabled={!showRemove}
        >
          <DeleteOutlined style={{ fontSize: "16px" }} />
        </button>
        <TheNoteToggleButton showNote={showNote} setShowNote={setShowNote} />
        <button
          type="button"
          className="btn btn-success btn-sm rounded-pill d-flex align-items-center justify-content-center p-0"
          style={{ width: "36px", height: "36px", flexShrink: 0 }}
          onClick={handleDownloadPNG}
          title="Download PNG"
          disabled={!barcodeInstance}
        >
          <DownloadOutlined style={{ fontSize: "16px" }} />
        </button>
      </div>
    </div>
  );
}

export default RenderOneBarcode;
